/**
 * SecureEmbed Energy Portal Login Page
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrendingUp, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { parseTierFromCognito } from '../constants/tiers';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [challengeSession, setChallengeSession] = useState<string | null>(null);

  useEffect(() => {
    const usernameParam = searchParams.get('username');
    if (usernameParam) {
      setUsername(usernameParam);
    }
    // Show session expired message if redirected from 401
    const reason = sessionStorage.getItem('auth_redirect_reason');
    if (reason) {
      setError(reason);
      sessionStorage.removeItem('auth_redirect_reason');
    }
  }, [searchParams]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      window.location.href = '/';
    }
  }, [navigate]);

  const decodeIdToken = (idToken: string): { tier: number; organization?: string; email?: string } => {
    try {
      const parts = idToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        return {
          tier: parseTierFromCognito(payload['custom:tier']),
          organization: payload['custom:organization'],
          email: payload.email,
        };
      }
    } catch {
      // Fall through
    }
    return { tier: 3 };
  };

  const storeAuthResult = (authResult: any, user: string) => {
    localStorage.setItem('authToken', authResult.IdToken);
    localStorage.setItem('idToken', authResult.IdToken);
    localStorage.setItem('refreshToken', authResult.RefreshToken || '');

    const decoded = decodeIdToken(authResult.IdToken);
    const userData = {
      username: user,
      email: decoded.email,
      tier: decoded.tier,
      organization: decoded.organization,
    };
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const config = window.SECUREEMBED_CONFIG;
    if (!config) {
      setError('Configuration not loaded. Please refresh the page.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `https://cognito-idp.${config.REGION}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
          },
          body: JSON.stringify({
            AuthFlow: 'USER_PASSWORD_AUTH',
            ClientId: config.COGNITO_CLIENT_ID,
            AuthParameters: {
              USERNAME: username,
              PASSWORD: password,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const errorType = data.__type || '';
        if (errorType.includes('NotAuthorizedException')) {
          throw new Error('Incorrect username or password');
        } else if (errorType.includes('UserNotFoundException')) {
          throw new Error('User does not exist');
        } else if (errorType.includes('UserNotConfirmedException')) {
          throw new Error('User is not confirmed. Please contact admin.');
        } else {
          throw new Error(data.message || 'Authentication failed');
        }
      }

      if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeSession(data.Session);
        setShowNewPasswordForm(true);
        setIsLoading(false);
        return;
      }

      if (data.AuthenticationResult) {
        storeAuthResult(data.AuthenticationResult, username);
        window.location.href = '/';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const config = window.SECUREEMBED_CONFIG;
    if (!config || !challengeSession) {
      setError('Session expired. Please try again.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `https://cognito-idp.${config.REGION}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
          },
          body: JSON.stringify({
            ChallengeName: 'NEW_PASSWORD_REQUIRED',
            ClientId: config.COGNITO_CLIENT_ID,
            Session: challengeSession,
            ChallengeResponses: {
              USERNAME: username,
              NEW_PASSWORD: newPassword,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to set new password');
      }

      if (data.AuthenticationResult) {
        storeAuthResult(data.AuthenticationResult, username);
        window.location.href = '/';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B064F] via-[#4A0E78] to-[#2B064F] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 backdrop-blur mb-4">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">The Energy Portal</h1>
          <p className="text-white/70">SecureEmbed Energy Platform</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
            {showNewPasswordForm ? 'Set New Password' : 'Sign In'}
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {showNewPasswordForm ? (
            <form onSubmit={handleNewPassword} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Your password must be changed before continuing.
              </p>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B064F] focus:border-transparent"
                    placeholder="Enter new password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-[#2B064F] text-white rounded-lg font-medium hover:bg-[#3D0A6B] transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Setting Password...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B064F] focus:border-transparent"
                  placeholder="Enter your username"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B064F] focus:border-transparent pr-12"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-[#2B064F] text-white rounded-lg font-medium hover:bg-[#3D0A6B] transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-white/50 text-sm">
            SecureEmbed Energy Portal 
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
